import React, { useState } from 'react';
import styled from 'styled-components';
import api from '../services/api';
import { toast } from 'react-toastify';

const Container = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: ${({ theme }) => theme.card};
  border: 2px solid ${({ theme }) => theme.primary};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  min-width: 400px;
`;

const Title = styled.h3`
  margin: 0 0 15px 0;
  color: ${({ theme }) => theme.primary};
  font-size: 1.1em;
`;

const DebugInfo = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 15px;
  font-family: monospace;
  font-size: 0.85em;
  
  div {
    margin: 4px 0;
  }
  
  strong {
    color: ${({ theme }) => theme.primary};
  }
`;

const FileInput = styled.input`
  display: block;
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.body};
  color: ${({ theme }) => theme.text};
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background: ${({ theme }) => theme.primary};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ theme }) => theme.primary}40;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LogArea = styled.div`
  margin-top: 15px;
  padding: 12px;
  background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)"};
  border-radius: 12px;
  max-height: 200px;
  overflow-y: auto;
  font-family: monospace;
  font-size: 0.8em;
  
  div {
    margin: 4px 0;
    padding: 4px;
    border-left: 3px solid ${({ theme }) => theme.primary};
    padding-left: 8px;
  }
`;

export default function XmlDownloadTest() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
    console.log(`[XmlDownloadTest] ${message}`);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    addLog(`📁 Arquivo selecionado: ${selectedFile?.name || 'nenhum'}`);
  };

  const handleUpload = async () => {
    console.log('🚀 === TESTE DE UPLOAD INICIADO ===');
    addLog('🚀 Iniciando teste de upload...');

    // 1. Verificar projeto
    const projectId = localStorage.getItem('vidal_selected_project');
    console.log('📋 Project ID:', projectId);
    addLog(`📋 Projeto no localStorage: ${projectId || '❌ NÃO ENCONTRADO'}`);

    if (!projectId) {
      console.error('❌ Nenhum projeto selecionado');
      addLog('❌ ERRO: Nenhum projeto selecionado');
      toast.error('Nenhum projeto selecionado');
      return;
    }

    // 2. Verificar arquivo
    if (!file) {
      console.error('❌ Nenhum arquivo selecionado');
      addLog('❌ ERRO: Nenhum arquivo selecionado');
      toast.error('Selecione um arquivo .txt');
      return;
    }

    console.log('📄 Arquivo:', file.name, `(${file.size} bytes)`);
    addLog(`📄 Arquivo: ${file.name} (${file.size} bytes)`);

    // 3. Verificar token
    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    console.log('🔑 Token existe:', token ? 'SIM' : 'NÃO');
    addLog(`🔑 Token: ${token ? '✅ Existe' : '❌ Não encontrado'}`);

    setLoading(true);

    try {
      // 4. Preparar FormData
      const formData = new FormData();
      formData.append('files', file);
      
      console.log('📤 Preparando requisição para /xml-download/upload');
      addLog('📤 Enviando requisição para API...');

      // 5. Fazer upload
      const response = await api.post('/xml-download/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('✅ Resposta recebida:', response.data);
      addLog(`✅ Resposta: ${JSON.stringify(response.data)}`);

      if (response.data.success) {
        toast.success('Upload realizado com sucesso!');
        addLog('🎉 Upload concluído com sucesso!');
      } else {
        toast.warning('Upload retornou success=false');
        addLog('⚠️ Upload retornou success=false');
      }

    } catch (error) {
      console.error('❌ ERRO no upload:', error);
      console.error('Detalhes:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      addLog(`❌ ERRO: ${error.message}`);
      addLog(`📊 Status: ${error.response?.status || 'N/A'}`);
      addLog(`📝 Detalhes: ${JSON.stringify(error.response?.data || {})}`);
      
      toast.error(error.response?.data?.error || 'Erro ao fazer upload');
    } finally {
      setLoading(false);
      console.log('🏁 === TESTE DE UPLOAD FINALIZADO ===');
      addLog('🏁 Teste finalizado');
    }
  };

  return (
    <Container>
      <Title>🧪 Teste de Upload XML</Title>
      
      <DebugInfo>
        <div><strong>Projeto:</strong> {localStorage.getItem('vidal_selected_project') || '❌ Não encontrado'}</div>
        <div><strong>Token:</strong> {JSON.parse(localStorage.getItem('vidal_auth') || '{}').token ? '✅ Existe' : '❌ Não encontrado'}</div>
        <div><strong>Arquivo:</strong> {file ? `✅ ${file.name}` : '❌ Nenhum'}</div>
      </DebugInfo>

      <FileInput
        type="file"
        accept=".txt"
        onChange={handleFileChange}
        disabled={loading}
      />

      <Button onClick={handleUpload} disabled={loading || !file}>
        {loading ? '⏳ Enviando...' : '🚀 Testar Upload'}
      </Button>

      {logs.length > 0 && (
        <LogArea>
          {logs.map((log, idx) => (
            <div key={idx}>{log}</div>
          ))}
        </LogArea>
      )}
    </Container>
  );
}
